import React from 'react';
import NoDataView from '../no-data-view';

/**
 * Scroll container for a bottom-paginated list. It owns the three states every
 * such list shares — first load, empty, and rows followed by the pagination
 * spinner — so a caller can't render the rows and forget the rest.
 *
 * The bottom spinner sticks to the bottom edge of the scrollport, so it is in
 * view the moment it appears without the list being scrolled under the user —
 * scrolling it into view instead fights the momentum that triggered the load.
 *
 * Pair it with `useInfiniteScroll` (directly, or through a hook like `useDocs`)
 * and pass that hook's `onScroll` and `isLoadingMore`.
 *
 * @param {string}   [className]     Added to the container; page styles hang off it.
 * @param {boolean}  [isLoading]     First load or a new search — replaces the rows.
 * @param {boolean}  [isLoadingMore] A page is on its way in.
 * @param {boolean}  [isEmpty]       Defaults to "no children given".
 */
const InfiniteScrollList = ({
    children,
    className = '',
    isLoading = false,
    isLoadingMore = false,
    isEmpty = React.Children.count(children) === 0,
    emptyText = 'No data found',
    emptySize = 'medium',
    onScroll,
}) => {
    return (
        <div className={`infinite-scroll-list ${className}`} onScroll={onScroll}>
            {isLoading ? (
                <div className="page-loader" />
            ) : isEmpty ? (
                <NoDataView noDataText={emptyText} size={emptySize} />
            ) : (
                <>
                    {children}
                    {isLoadingMore && (
                        <div className="infinite-scroll-list-loader">
                            <div className="page-loader" />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default InfiniteScrollList;
